import type React from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
} from "@mui/material";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { CrewEntry } from "../components/callsheet/types/callsheet";
import { RootState } from "../../../store/Index";
import { BaseURL } from "../../../service/Api";

interface CrewAssignmentTableProps {
  crewMembersData: CrewEntry[];
  streamId?: number;
}

const CrewAssignmentTable: React.FC<CrewAssignmentTableProps> = ({
  crewMembersData,
  streamId,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  console.log("crew data", crewMembersData);

  const roleOrder = [
    "Executive Producer",
    "Producer",
    "Assistant Producer",
    "Liveswitcher",
    "Camera",
    "Networking",
    "Sound",
    "Tribute Table",
  ];

  const sortedCrewMembers = [...crewMembersData].sort((a, b) => {
    const roleA = a.roles?.[0]?.name || a.assignment?.[0]?.role || "N/A";
    const roleB = b.roles?.[0]?.name || b.assignment?.[0]?.role || "N/A";

    const indexA = roleOrder.findIndex(
      (role) => role.toLowerCase() === roleA.toLowerCase()
    );
    const indexB = roleOrder.findIndex(
      (role) => role.toLowerCase() === roleB.toLowerCase()
    );

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  const handleSendCallsheet = () => {
    const pdfLink = `${BaseURL}/stream-management/view/${streamId}/callsheet`;

    const message = encodeURIComponent(
      `Hello, this is your callsheet for the event.\n\nDownload PDF here:\n${pdfLink}`
    );

    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  return (
    <Card sx={{ boxShadow: 0, height: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {" "}
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Crew Assignment
          </Typography>
          <Button
            sx={{
              backgroundColor: reduxTheme.primary_colors[400],
              color: reduxTheme.primary_colors[900],
            }}
            onClick={() => handleSendCallsheet()}
          >
            Send Callsheet
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{ backgroundColor: reduxTheme.primary_colors[800] }}
              >
                <TableCell
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  Position
                </TableCell>
                <TableCell
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  Email
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCrewMembers.map((entry, index) => {
                const crewMember = entry.crew_member;
                const user = crewMember.user;

                const role =
                  entry?.roles?.[0]?.name ||
                  entry?.assignment?.[0]?.role ||
                  "N/A";

                const fullName = `${user.first_name} ${user.last_name}`.trim();
                const email = user.email || "";

                return (
                  <motion.tr
                    key={entry.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1 }}
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? "transparent"
                          : reduxTheme.primary_colors[800],
                      cursor: "pointer",
                    }}
                  >
                    <TableCell sx={{ fontSize: "0.85rem" }}>
                      {fullName}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.85rem" }}>{role}</TableCell>
                    <TableCell sx={{ fontSize: "0.85rem" }}>{email}</TableCell>
                  </motion.tr>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default CrewAssignmentTable;
